import { Modal, Form, Input, DatePicker, Select, Button, Row, Col } from 'antd';
import { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import apiService from '../api/apiService';
import { toast } from 'react-toastify';

dayjs.extend(customParseFormat);

const EditBookingModal = ({
  initialValues,
  loading,
  editingItem,
  bookings,
  setBookings,
  hotelId,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRoomPrice, setSelectedRoomPrice] = useState(null);
  const [rooms, setRooms] = useState([]);

  const bookedRoomIds = bookings.map(booking => booking.room.id);
  const availableRooms = rooms.filter(room => !bookedRoomIds.includes(room.id));

  const [checkInDate, setCheckInDate] = useState(
    initialValues?.checkIn || null
  );
  const [checkOutDate, setCheckOutDate] = useState(
    initialValues?.checkOut || null
  );

  useEffect(() => {
    fetchRooms();
  }, []);

  // Update form when editing an item
  useEffect(() => {
    if (editingItem) {
      const formattedValues = {
        checkIn: dayjs(editingItem.check_in, 'YYYY-MM-DD'),
        checkOut: dayjs(editingItem.check_out, 'YYYY-MM-DD'),
        /* room: `Room ${editingItem.room.room_number} - ${editingItem.room.room_type.name}`, */
        amount: editingItem.amount,
        source: editingItem.source,
        status: editingItem.status,
        guest: editingItem.guest,
      };
      form.setFieldsValue(formattedValues);
      setCheckInDate(formattedValues.checkIn);
      setCheckOutDate(formattedValues.checkOut);
    }
  }, [editingItem, form, isModalOpen]);

  const handleSubmit = async values => {
    try {
      const bookingData = {
        status: values.status,
        hotel: localStorage.getItem('hotel'),
      };

      // Update the main booking status
      const bookingResponse = await apiService.patch(
        `/bookings/${editingItem.id}/?hotel=${hotelId}`,
        bookingData
      );
      onSuccess();

      toast.success('Booking updated successfully!');

      // Handle room status updates for each room in the array
      if (editingItem.room && Array.isArray(editingItem.room)) {
        // If room is an array, process each room
        for (const room of editingItem.room) {
          await updateRoomStatus(room, values.status);
        }
      } else if (editingItem.room) {
        // For backward compatibility if room is a single object
        await updateRoomStatus(editingItem.room, values.status);
      }

      setBookings(prevBookings =>
        prevBookings.map(item =>
          item.id === editingItem.id ? { ...item, ...bookingResponse } : item
        )
      );

      form.resetFields();
      setIsModalOpen(false);
    } catch (error) {
      toast.error('Failed to update booking!');
      console.error('Error while updating booking:', error);
    }
  };

  // Helper function to update room status
  const updateRoomStatus = async (room, bookingStatus) => {
    try {
      const newStatus = bookingStatus === 'check_in' ? 'occupied' : 'available';

      // Only update if the room's current status is different
      if (room.status !== newStatus) {
        const updatedRoomStatus = { status: newStatus };
        await apiService.patch(
          `/rooms/${room.id}/?hotel=${hotelId}`,
          updatedRoomStatus
        );
        onSuccess();

        console.log(`Room ${room.room_number} status updated to ${newStatus}`);

        // Show toast notification after successful update
        setTimeout(() => {
          toast.success(
            `Room ${room.room_number} status updated to ${
              newStatus.charAt(0).toUpperCase() + newStatus.slice(1)
            }!`
          );
        }, 500);
      } else {
        console.log(
          `Room ${room.room_number} status is already up to date, no patch needed.`
        );
      }
    } catch (error) {
      console.error(`Failed to update room ${room.room_number} status:`, error);
      toast.error(`Failed to update room ${room.room_number} status!`);
    }
  };

  const fetchRooms = async () => {
    try {
      const response = await apiService.get(`/rooms/?hotel=${hotelId}`);
      setRooms(response);
    } catch (error) {
      console.error('Error fetching rooms:', error);
    }
  };

  return (
    <>
      <button onClick={() => setIsModalOpen(true)}>Edit</button>
      <Modal
        title="Edit Booking"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={initialValues}
          className="avail-form"
        >
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Check In"
                name="checkIn"
                rules={[
                  { required: true, message: 'Please select check-in date' },
                ]}
              >
                <DatePicker
                  disabled={true}
                  format="YYYY/MM/DD"
                  style={{ width: '100%' }}
                  disabledDate={current =>
                    current && current < dayjs().startOf('day')
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Check Out"
                name="checkOut"
                rules={[
                  { required: true, message: 'Please select check-out date' },
                ]}
              >
                <DatePicker
                  disabled={true}
                  format="YYYY/MM/DD"
                  style={{ width: '100%' }}
                  disabledDate={current =>
                    checkInDate &&
                    current &&
                    current <= checkInDate.startOf('day')
                  }
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label="Room(s)" name="room">
                <Input
                  disabled={true}
                  value={
                    Array.isArray(editingItem?.room)
                      ? editingItem.room
                          .map(r => `Room ${r.room_number}`)
                          .join(', ')
                      : editingItem?.room
                      ? `Room ${editingItem.room.room_number}`
                      : ''
                  }
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Amount" name="amount">
                <Input disabled={true} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Source"
                name="source"
                rules={[{ required: true, message: 'Please select source' }]}
              >
                <Select
                  disabled={true}
                  placeholder="Select source"
                  style={{ marginBottom: '8px' }}
                >
                  <Select.Option value="website">Website</Select.Option>
                  <Select.Option value="agent">Agent</Select.Option>
                  <Select.Option value="walk_in">Walk-in</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Status"
                name="status"
                rules={[{ required: true, message: 'Please select status' }]}
              >
                <Select
                  placeholder="Select status"
                  style={{ marginBottom: '8px' }}
                >
                  <Select.Option value="confirmed">Confirmed</Select.Option>
                  <Select.Option value="pending">Pending</Select.Option>
                  <Select.Option value="cancelled">Cancelled</Select.Option>
                  <Select.Option value="check_in">Checked-in</Select.Option>
                  <Select.Option value="check_out">Checked-out</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24}>
              <div className="avail-button-group">
                <Button
                  type="default"
                  onClick={() => setIsModalOpen(false)}
                  className="avail-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  className="avail-save-btn"
                >
                  Save
                </Button>
              </div>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default EditBookingModal;
